/**
 * Modal opened from ProposalDetail when the rep clicks "Send Contract" on
 * an approved proposal. Collects the commercial terms (operator signer
 * email + name + title, client_id, lots, platform fee bps, monthly fee,
 * term length, start date), POSTs /contract/from-proposal/<pid>, then
 * POSTs /contract/<cid>/send. On success, navigates to the contract
 * detail screen.
 *
 * Form layout follows the proposal-portal's vertical-rhythm scheme — same
 * label/eyebrow conventions as ProposalFields so the experience feels
 * adjacent, not separate.
 */
import { useState } from 'react'
import {
  generateContractFromProposal,
  sendContract,
  type ContractTerm,
  type FromProposalBody,
  type ContractRecord,
} from '../api/contracts'
import { useAuth } from '../auth/AuthContext'
import { colors } from '../theme'
import { DatePicker } from '@parkify/ui'

interface Props {
  proposalId: string
  initialOperatorName: string
  initialOperatorEmail?: string
  onClose: () => void
  onSent: (c: ContractRecord) => void
}

const TERMS: { value: ContractTerm; label: string }[] = [
  { value: 'monthly', label: 'Month-to-month' },
  { value: '1yr', label: '1 year' },
  { value: '2yr', label: '2 years' },
  { value: '3yr', label: '3 years' },
  { value: 'custom', label: 'Custom (specify)' },
]

function todayIso(): string {
  // YYYY-MM-DD in local time. Defaults the start_date input to today —
  // reps almost always pick "today or later" so this saves a click.
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

export default function SendContractModal({
  proposalId,
  initialOperatorName,
  initialOperatorEmail,
  onClose,
  onSent,
}: Props) {
  const { authHeaders, apiBase } = useAuth()
  const [operatorEmail, setOperatorEmail] = useState(initialOperatorEmail ?? '')
  const [signerName, setSignerName] = useState('')
  const [signerTitle, setSignerTitle] = useState('')
  const [clientId, setClientId] = useState('')
  const [lotsText, setLotsText] = useState(initialOperatorName)
  const [platformFeeBps, setPlatformFeeBps] = useState('250')
  const [monthlyFee, setMonthlyFee] = useState('500')
  const [term, setTerm] = useState<ContractTerm>('1yr')
  const [termDescription, setTermDescription] = useState('')
  const [startDate, setStartDate] = useState(todayIso())
  const [stage, setStage] = useState<'idle' | 'generating' | 'sending'>('idle')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const lotLabels = lotsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)

    if (!operatorEmail || !signerName || !clientId || lotLabels.length === 0) {
      setError(
        'Operator email, signer name, client ID, and at least one lot label are required.',
      )
      return
    }
    const feeBps = Number(platformFeeBps)
    const monthly = Number(monthlyFee)
    if (Number.isNaN(feeBps) || feeBps < 0 || feeBps > 10000) {
      setError('Platform fee (bps) must be a number between 0 and 10000.')
      return
    }
    if (Number.isNaN(monthly) || monthly < 0) {
      setError('Monthly fee must be a non-negative number.')
      return
    }
    if (term === 'custom' && !termDescription.trim()) {
      setError('When term is "Custom", describe the term in the field below.')
      return
    }

    const body: FromProposalBody = {
      operator_email: operatorEmail.trim(),
      operator_signer_name: signerName.trim(),
      operator_signer_title: signerTitle.trim() || undefined,
      client_id: clientId.trim(),
      lot_labels: lotLabels,
      platform_fee_bps: feeBps,
      monthly_fee_usd: monthly,
      term,
      term_description: termDescription.trim() || undefined,
      start_date: startDate,
    }

    try {
      setStage('generating')
      const created = await generateContractFromProposal(
        apiBase,
        authHeaders(),
        proposalId,
        body,
      )
      setStage('sending')
      const sent = await sendContract(apiBase, authHeaders(), created.id)
      onSent(sent)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed')
      setStage('idle')
    }
  }

  const busy = stage !== 'idle'

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35,31,32,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 100,
      }}
      onClick={() => !busy && onClose()}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        style={{
          background: colors.white,
          borderRadius: 16,
          maxWidth: 640,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 24px 64px rgba(35,31,32,0.32)',
          padding: 32,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: '2.5px',
            fontWeight: 700,
            color: colors.purple,
            marginBottom: 8,
            textTransform: 'uppercase',
          }}
        >
          Send contract
        </div>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.5px',
            margin: '0 0 6px',
            color: colors.black,
          }}
        >
          {initialOperatorName}
        </h2>
        <p
          style={{
            fontSize: 12,
            color: colors.textMuted,
            margin: '0 0 22px',
            lineHeight: 1.5,
          }}
        >
          Confirm the commercial terms. We&apos;ll generate the contract from
          the legal-reviewed template, send it via Documenso for signature,
          and track the status on the contract page.
        </p>

        {error ? (
          <div
            style={{
              padding: '12px 16px',
              background: colors.warningBg,
              border: `1.5px solid ${colors.warningBorder}`,
              borderRadius: 10,
              color: colors.black,
              fontSize: 13,
              marginBottom: 18,
            }}
          >
            <span style={{ color: colors.red, fontWeight: 700, marginRight: 6 }}>
              ⚠
            </span>
            {error}
          </div>
        ) : null}

        <Row label="Operator signer name" required>
          <input
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Jane Doe"
            disabled={busy}
            style={input()}
          />
        </Row>
        <Row label="Operator signer title">
          <input
            value={signerTitle}
            onChange={(e) => setSignerTitle(e.target.value)}
            placeholder="Owner / Property Manager / etc."
            disabled={busy}
            style={input()}
          />
        </Row>
        <Row label="Signing email" required>
          <input
            value={operatorEmail}
            onChange={(e) => setOperatorEmail(e.target.value)}
            type="email"
            placeholder="jane@operator.com"
            disabled={busy}
            style={input()}
          />
        </Row>
        <Row
          label="Client ID"
          required
          hint="Stable identifier the Stripe Connect side will key off. e.g. operator-364-thames"
        >
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="operator-364-thames"
            disabled={busy}
            style={input()}
          />
        </Row>
        <Row label="Lot labels" hint="One per line.">
          <textarea
            value={lotsText}
            onChange={(e) => setLotsText(e.target.value)}
            disabled={busy}
            rows={3}
            style={{ ...input(), resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Row>

        <div style={{ display: 'flex', gap: 12 }}>
          <Row label="Platform fee (bps)" hint="250 = 2.5%">
            <input
              value={platformFeeBps}
              onChange={(e) => setPlatformFeeBps(e.target.value)}
              type="number"
              min="0"
              max="10000"
              disabled={busy}
              style={input()}
            />
          </Row>
          <Row label="Monthly fee (USD)">
            <input
              value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)}
              type="number"
              min="0"
              step="0.01"
              disabled={busy}
              style={input()}
            />
          </Row>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Row label="Term">
            <select
              value={term}
              onChange={(e) => setTerm(e.target.value as ContractTerm)}
              disabled={busy}
              style={input()}
            >
              {TERMS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Start date">
            <DatePicker
              value={startDate}
              onChange={(date) => setStartDate(date ?? '')}
              disabled={busy}
            />
          </Row>
        </div>

        {term === 'custom' ? (
          <Row label="Custom term description" required>
            <input
              value={termDescription}
              onChange={(e) => setTermDescription(e.target.value)}
              placeholder="e.g. 18 months, auto-renewing"
              disabled={busy}
              style={input()}
            />
          </Row>
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 24,
          }}
        >
          <button
            type="button"
            onClick={() => !busy && onClose()}
            disabled={busy}
            style={btnSecondary(busy)}
          >
            Cancel
          </button>
          <button type="submit" disabled={busy} style={btnPrimary(busy)}>
            {stage === 'generating'
              ? 'Generating…'
              : stage === 'sending'
              ? 'Sending to Documenso…'
              : 'Generate + Send'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Row({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 16, flex: 1 }}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          color: colors.purple,
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
        {required ? (
          <span style={{ color: colors.red, marginLeft: 4 }}>*</span>
        ) : null}
      </label>
      {children}
      {hint ? (
        <div
          style={{
            fontSize: 11,
            color: colors.textDim,
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  )
}

function input(): React.CSSProperties {
  return {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    fontSize: 13,
    border: `1px solid ${colors.divider}`,
    borderRadius: 8,
    color: colors.black,
    background: colors.white,
  }
}

function btnPrimary(busy: boolean): React.CSSProperties {
  return {
    background: colors.purple,
    color: colors.white,
    border: 'none',
    borderRadius: 8,
    padding: '12px 22px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    opacity: busy ? 0.6 : 1,
    cursor: busy ? 'wait' : 'pointer',
  }
}

function btnSecondary(busy: boolean): React.CSSProperties {
  return {
    background: colors.white,
    color: colors.black,
    border: `1px solid ${colors.divider}`,
    borderRadius: 8,
    padding: '12px 22px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    opacity: busy ? 0.5 : 1,
    cursor: busy ? 'not-allowed' : 'pointer',
  }
}
